<template>
    <div v-if="trainerPosition" class="lo-root" :style="bgStyle">
        <div class="lo-content">
            <div v-if="league && league.nextOpponent" class="lo-round-card transparent07--dark">
                <div class="lo-vs-pill" :style="vsPillStyle">
                    {{ $t('journey.leagueOak.fightVsPrefix') }} <span class="lo-caps">{{ league.nextOpponent.fullName }}</span> {{ $t('journey.leagueOak.fightAtPrefix') }} {{ stadionPrefix }}<span class="lo-caps">{{ stadionTypeName }}</span> {{ stadionSuffix }}
                </div>
                <div class="lo-round-body">
                    <v-img
                        v-if="league.nextOpponent.img"
                        :src="require(`@/assets/pic/trainer/${league.nextOpponent.img}`)"
                        class="lo-opponent-img"
                        contain
                    />
                    <div class="lo-round-info">
                        <div class="lo-round-title">{{ roundTitle }}</div>
                        <ul class="lo-rules-list">
                            <li>{{ pokemonRuleCount }}/6 {{ $t('journey.leagueOak.pokemonRuleSuffix') }}</li>
                            <li>{{ $t('journey.leagueOak.oneVsOne') }}</li>
                            <li>{{ $t('journey.leagueOak.lastManStanding') }}</li>
                        </ul>
                        <div class="lo-selected" :class="{'lo-selected--warning': notEnoughSelected}">{{ $t('journey.leagueOak.selectedLabel') }} {{ trainerPokemonsCount }}/{{ requiredPokemons }}</div>
                    </div>
                </div>
                <v-btn
                    large
                    rounded
                    block
                    :loading="isLoading"
                    :disabled="notEnoughSelected || isLoading"
                    color="black--text"
                    class="gold-btn btn-shadow lo-fight-btn"
                    @click="fight()"
                >
                    {{ notEnoughSelected ? $tc('journey.leagueOak.missingPokemonsButton', missingPokemonsCount, {n: missingPokemonsCount}) : $t('journey.leagueOak.startFight') }}
                </v-btn>
            </div>

            <div v-else-if="league" class="lo-round-card lo-round-card--error transparent08--dark">
                {{ $t('journey.leagueOak.emptyQueue') }}
            </div>

            <div class="lo-oak-card">
                <OakTable :trainer-id="trainerPosition.trainer.id" @team-changed="refreshTeamSheet" />
            </div>
        </div>
    </div>
</template>

<script>
    // Fork LeagueOak obrazovky - stará LeagueOak.vue zůstává zmrazená/referenční
    // za !flags.newUi. Viz claude/components/journey-oak-league.md.
    import {mapActions, mapGetters} from 'vuex';
    import * as journeyApi from '@/api/journeyApi';
    import {TYPE_COLORS, TYPE_DARK_THEME} from '@/utils/typeColorEnum';
    import OakTable from './pieces/OakTable';

    export default {
        name: 'LeagueOakNext',
        components: {OakTable},
        props: {
            trainerPosition: {type: Object, default: null},
        },
        computed: {
            ...mapGetters({
                isLoading: 'journey/isLoading',
                pokemons: 'pokemon/items',
            }),
            league() {
                return this.trainerPosition.league;
            },
            trainerPokemonsCount() {
                return this.pokemons.length;
            },
            requiredPokemons() {
                return Math.min(6, this.trainerPosition.trainer.havingPokemonsCount);
            },
            bgStyle() {
                return {backgroundImage: `url(${require('@/assets/pic/web/league_stadion.png')})`};
            },
            stadionTypeName() {
                const stadion = this.league.nextStadion;
                return stadion && stadion.type ? this.$t('common.typeNames.' + stadion.type.name) : '';
            },
            stadionPrefix() {
                return this.league.kvalification ? '' : this.$t('journey.leagueOak.finalPrefix');
            },
            stadionSuffix() {
                return this.league.kvalification ? this.$t('journey.leagueOak.qualificationStadionSuffix') : this.$t('journey.leagueOak.stadionSuffix');
            },
            vsPillStyle() {
                const stadion = this.league.nextStadion;
                if (!stadion || !stadion.type || !TYPE_COLORS[stadion.type.id]) {
                    return {};
                }
                return {
                    backgroundColor: TYPE_COLORS[stadion.type.id],
                    color: TYPE_DARK_THEME[stadion.type.id] ? '#fff' : '#000',
                };
            },
            roundTitle() {
                if (this.league.kvalification) {
                    return this.$t('journey.leagueOak.roundOfQualification', {n: this.league.round + 1});
                }
                return this.league.round >= 11
                    ? this.$t('journey.leagueOak.finalBattle')
                    : this.$t('journey.leagueOak.roundOfTournament', {n: this.league.round + 1});
            },
            pokemonRuleCount() {
                if (this.league.kvalification) {
                    return 3;
                }
                return this.league.round >= 11 ? 6 : 3;
            },
            notEnoughSelected() {
                return this.trainerPokemonsCount < this.requiredPokemons;
            },
            missingPokemonsCount() {
                return this.requiredPokemons - this.trainerPokemonsCount;
            },
        },
        created() {
            this.trainerInfoMessages(this.trainerPosition.trainer.id);
        },
        methods: {
            ...mapActions({
                positionPost: 'journey/positionPost',
                trainerInfoMessages: 'trainer/loadInfoMessages',
                refreshActiveTeam: 'pokemon/refreshActiveTeam',
            }),
            fight() {
                this.positionPost(journeyApi.meetLeagueTrainer(this.trainerPosition.id));
            },
            refreshTeamSheet() {
                this.refreshActiveTeam();
            },
        },
    }
</script>

<style scoped>
.lo-root {
    position: fixed;
    inset: 0;
    height: 100%;
    background-size: cover;
    background-position: center;
    overflow-y: auto;
}
.lo-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    /* bottom 54px = rezerva pro globální TeamBottomSheet - .lo-root je "position: fixed; inset: 0",
       počítá se proti skutečnému viewportu, ne 22px jako TEAM_SHEET_HANDLE_HEIGHT v normálním flow */
    padding: 74px 16px 54px 16px;
    box-sizing: border-box;
    min-height: 100%;
}
.lo-round-card {
    width: 100%;
    max-width: 420px;
    border-radius: 18px;
    padding: 0 14px 14px 14px;
    box-shadow: 2px 3px 10px rgba(0,0,0,0.35);
    box-sizing: border-box;
    overflow: hidden;
    /* "žíhání" (pruhovaný gradient) - stejný recept jako popupy (ResponsiveDialog.vue),
       jen aplikovaný i mimo popup; skládá se NAD background-color (transparent07--dark), ne místo něj */
    background-image:
        radial-gradient(circle at 50% 0%, rgba(33,168,122,0.1) 0%, transparent 55%),
        linear-gradient(
            140deg,
            rgba(0,0,0,0.02) 0%,
            rgba(0,0,0,0.22) 25%,
            rgba(0,0,0,0.02) 50%,
            rgba(0,0,0,0.22) 75%,
            rgba(0,0,0,0.02) 100%
        );
}
/* fallback, když je league.nextOpponent null (fronta soupeřů prázdná) - .lo-round-card normálně počítá
   s .lo-vs-pill jako "hlavičkou" (padding-top: 0 na rodiči), tady žádná není, potřebuje vlastní padding-top */
.lo-round-card--error {
    padding: 14px;
    text-align: center;
    font-weight: 700;
}
.lo-vs-pill {
    margin: 0 -14px 10px -14px;
    padding: 8px 14px;
    font-size: 15px;
    font-weight: 700;
    text-align: center;
    /* stejne zihani jako .lo-round-card vyse, nad dynamickou barvou z vsPillStyle */
    background-image:
        radial-gradient(circle at 50% 0%, rgba(33,168,122,0.1) 0%, transparent 55%),
        linear-gradient(
            140deg,
            rgba(0,0,0,0.02) 0%,
            rgba(0,0,0,0.22) 25%,
            rgba(0,0,0,0.02) 50%,
            rgba(0,0,0,0.22) 75%,
            rgba(0,0,0,0.02) 100%
        );
    /* barva textu je dynamicka (vsPillStyle) podle TYPE_DARK_THEME - stejny
       princip jako EnvironmentPill.vue/NextRoundTile.vue (tmave pozadi typu,
       napr. WATER, potrebuje bily text, ne cerny) */
}
.lo-caps {
    text-transform: uppercase;
}
.lo-round-body {
    display: flex;
    align-items: center;
    gap: 10px;
}
.lo-opponent-img {
    flex: 0 0 40%;
    max-width: 40%;
    max-height: 130px;
}
.lo-round-info {
    flex: 1 1 auto;
    min-width: 0;
    color: #fff;
}
.lo-round-title {
    font-size: 17px;
    font-weight: 800;
    margin-bottom: 4px;
}
.lo-rules-list {
    margin: 0 0 6px 18px;
    padding: 0;
    font-size: 13px;
}
.lo-selected {
    font-size: 14px;
    font-weight: 700;
}
.lo-selected--warning {
    color: #FF5252;
}
.lo-fight-btn {
    margin-top: 12px;
}
.lo-oak-card {
    width: 100%;
    max-width: 420px;
}
</style>
